import { useEffect, useMemo, useState } from 'react';
import { useForm, type FieldPath } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CENTRO_CLASSIFICACOES,
  CENTRO_NATUREZAS,
  CENTRO_TIPOS_NO,
  CENTRO_TRABALHO_TIPOS,
  centroArvoreFormSchema,
  ROTULO_CENTRO_CLASSIFICACAO,
  ROTULO_CENTRO_NATUREZA,
  ROTULO_CENTRO_TIPO_NO,
  ROTULO_CENTRO_TRABALHO,
  type CentroArvoreFormValores,
  type CentroNoResponse,
  type CentroTipoNo,
  type ExcluirCentroResponse,
  type MinutosCentroResponse,
} from '@triade/contracts';
import { useAtualizarCentro, useCriarCentro, useExcluirCentro, useMinutosCentro, useUsoCentro } from '@/api/centros-custo';
import { CodigosExternos } from '@/components/externos/codigos-externos';
import { MensagemCampo } from '@/components/item-cadastro/dados-item';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { formatarQuantidade } from '@/lib/format';
import { restricoesTipo, rotuloCentro } from './arvore-logica';
import { dtoAtualizar, dtoCriar, formDoCentro, formNovo, limparParaTipo } from './centro-form-dados';
import { mensagemCentro, motivosDoUso } from './mensagens';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const SELECT =
  'h-9 w-full rounded-md border border-input bg-card px-3 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50';
const DICA = 'mt-1 text-caption text-ink-muted';

export interface CentroFormProps {
  /** Centro aberto; ausente = centro novo. */
  centro?: CentroNoResponse | undefined;
  /** Centro novo com pai (botao "+ filho"); ausente = raiz. */
  pai?: CentroNoResponse | undefined;
  /** Quantos filhos o centro tem (da arvore): trava a troca para analitico. */
  filhos: number;
  /** Sem `centro.editar` tudo fica desabilitado e sem barra de registro. */
  editavel: boolean;
  onCriado: (centro: CentroNoResponse) => void;
  /** "Mover para…" (so com centro aberto). */
  onMover?: (() => void) | undefined;
  /** Excluir (menu ⋯): a confirmacao mostra o uso; o resultado (excluido ou desativado) vai para a tela. */
  onExcluido?: ((resultado: ExcluirCentroResponse, centro: CentroNoResponse) => void) | undefined;
  onNovo: () => void;
}

/**
 * Formulario do centro na arvore (react-hook-form + `centroArvoreFormSchema` de @triade/contracts). Regras que a tela espelha da
 * API (a API e quem recusa): sintetico so agrupa (sem minutos nem area); classificacao so em analitico produtivo; tipo de trabalho
 * obrigatorio no analitico (qualquer natureza; a API recusa com TIPO_TRABALHO_OBRIGATORIO); o tipo muda so quando o uso e a arvore
 * deixam. Minutos do analitico salvo vem de /minutos (calculado = so leitura; manual = editavel). Nenhum valor e calculado aqui.
 */
export function CentroForm({ centro, pai, filhos, editavel, onCriado, onMover, onExcluido, onNovo }: CentroFormProps) {
  const base = useMemo(() => (centro ? formDoCentro(centro) : formNovo()), [centro]);
  const form = useForm<CentroArvoreFormValores>({ resolver: zodResolver(centroArvoreFormSchema), defaultValues: base, mode: 'onTouched' });
  const { isDirty, errors, dirtyFields } = form.formState;
  const tipoNo = form.watch('tipoNo');
  const natureza = form.watch('natureza');
  const classificacao = form.watch('classificacao');
  const ativo = form.watch('ativo');
  const uso = useUsoCentro(centro?.id);
  // Minutos calculados so existem para analitico salvo (o novo nao tem alocacao ainda).
  const idMinutos = centro?.tipoNo === 'ANALITICO' ? centro.id : undefined;
  const minutos = useMinutosCentro(idMinutos);
  const criar = useCriarCentro();
  const atualizar = useAtualizarCentro();
  const excluir = useExcluirCentro();
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // Depois de criar, a tela vai para o centro novo: nao ha alteracao a perder. Desligar o bloqueio antes de navegar evita o
  // "Sair sem salvar?" falso (o navegador consulta o estado sujo antes do reset).
  const [criado, setCriado] = useState<CentroNoResponse | null>(null);
  const bloqueio = useBlockerDirty(isDirty && criado === null);
  useEffect(() => {
    if (criado) onCriado(criado);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criado]);

  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const restr = restricoesTipo(centro, uso.data, filhos);
  const analitico = tipoNo === 'ANALITICO';
  const produtivo = natureza === 'PRODUTIVO';
  const desabilitado = !editavel;
  const travaMinutosArea = desabilitado || !analitico;
  const minutosDoCentro = idMinutos !== undefined && analitico;

  /** Aplica so os campos informados, marcando como alterados (sem reset: as outras edicoes continuam). */
  function aplicar(patch: Partial<CentroArvoreFormValores>) {
    const o = { shouldDirty: true, shouldValidate: true } as const;
    if (patch.tipoNo !== undefined) form.setValue('tipoNo', patch.tipoNo, o);
    if (patch.natureza !== undefined) form.setValue('natureza', patch.natureza, o);
    if (patch.classificacao !== undefined) form.setValue('classificacao', patch.classificacao, o);
    if (patch.tipoTrabalho !== undefined) form.setValue('tipoTrabalho', patch.tipoTrabalho, o);
    if (patch.areaM2 !== undefined) form.setValue('areaM2', patch.areaM2, o);
    if (patch.minutosDisponiveisMes !== undefined) form.setValue('minutosDisponiveisMes', patch.minutosDisponiveisMes, o);
  }

  function aoMudarTipo(valor: CentroTipoNo) {
    aplicar(limparParaTipo({ ...form.getValues(), tipoNo: valor }));
  }

  function aoMudarNatureza(valor: CentroArvoreFormValores['natureza']) {
    aplicar(valor === 'PRODUTIVO' ? { natureza: valor } : { natureza: valor, classificacao: '' });
  }

  function aoMudarClassificacao(valor: CentroArvoreFormValores['classificacao']) {
    aplicar({ classificacao: valor });
  }

  async function gravar(v: CentroArvoreFormValores) {
    setErro(null);
    try {
      if (!centro) {
        const novo = await criar.mutateAsync(dtoCriar(v, pai?.id ?? null));
        setSalvoEm(new Date());
        form.reset(formDoCentro(novo));
        setCriado(novo);
        return;
      }
      const gravado = await atualizar.mutateAsync({ id: centro.id, dto: dtoAtualizar(v) });
      form.reset(formDoCentro(gravado));
      setSalvoEm(new Date());
    } catch (e) {
      setErro(mensagemCentro(e));
      if (e instanceof ApiError && e.code === 'IDENTIFICADOR_DUPLICADO') form.setError('identificador', { message: 'Já existe um irmão com este identificador' });
    }
  }

  const salvar = () => void form.handleSubmit(gravar, () => setErro('Corrija os campos destacados'))();

  async function aoExcluir() {
    if (!centro || !onExcluido) return;
    setErro(null);
    try {
      onExcluido(await excluir.mutateAsync(centro.id), centro);
    } catch (e) {
      setErro(mensagemCentro(e));
    }
  }
  const podeExcluir = uso.data?.podeExcluir ?? true;
  const motivos = uso.data ? motivosDoUso(uso.data) : [];
  const titulo = centro ? rotuloCentro(centro) : pai ? `Novo filho de ${rotuloCentro(pai)}` : 'Novo centro na raiz';
  const dica = analitico
    ? 'Analítico recebe minutos disponíveis, CPM e etapas de ficha.'
    : 'Sintético só agrupa: não recebe minutos, CPM nem etapas.';
  const desativaFilhos = centro?.tipoNo === 'SINTETICO' && centro.ativo && !ativo && filhos > 0;

  return (
    <section aria-labelledby="titulo-centro" data-testid="detalhe-centro" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="titulo-centro" className="text-title font-semibold">
            {titulo}
          </h2>
          <p className="mt-0.5 text-small text-ink-secondary">{dica}</p>
          {centro && !centro.ativo && <p className="mt-0.5 text-small text-ink-secondary">Centro inativo: não recebe CPM novo nem entra nas grades (marque Ativo para reativar).</p>}
        </div>
        {centro && editavel && onMover && (
          <Button type="button" variant="outline" size="sm" onClick={onMover}>
            Mover para…
          </Button>
        )}
      </header>

      <form onSubmit={(e) => { e.preventDefault(); salvar(); }} noValidate aria-label="Dados do centro de custo" className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
        <div>
          <Label htmlFor="c-identificador" className={ROTULO}>Identificador</Label>
          <Input id="c-identificador" maxLength={40} disabled={desabilitado} aria-invalid={errors.identificador ? true : undefined} {...form.register('identificador')} />
          <MensagemCampo id="c-identificador-erro" mensagem={errors.identificador?.message} />
          <p className={DICA}>Segmento local (único entre irmãos). O caminho (ex.: 3.1.2) sai da árvore.</p>
        </div>
        <div className="lg:col-span-2">
          <Label htmlFor="c-nome" className={ROTULO}>Nome</Label>
          <Input id="c-nome" maxLength={200} disabled={desabilitado} aria-invalid={errors.nome ? true : undefined} {...form.register('nome')} />
          <MensagemCampo id="c-nome-erro" mensagem={errors.nome?.message} />
        </div>

        <div>
          <Label htmlFor="c-tipo" className={ROTULO}>Tipo</Label>
          <select id="c-tipo" className={SELECT} disabled={desabilitado} value={tipoNo} onChange={(e) => aoMudarTipo(e.target.value as CentroTipoNo)} aria-describedby="c-tipo-dica">
            {CENTRO_TIPOS_NO.map((t) => {
              const motivo = t === 'SINTETICO' ? restr.paraSintetico : restr.paraAnalitico;
              return (
                <option key={t} value={t} disabled={t !== tipoNo && motivo !== null}>
                  {ROTULO_CENTRO_TIPO_NO[t]}
                  {t !== tipoNo && motivo !== null ? ' (não disponível)' : ''}
                </option>
              );
            })}
          </select>
          <p id="c-tipo-dica" className={DICA}>
            {(tipoNo === 'SINTETICO' ? restr.paraAnalitico : restr.paraSintetico) ?? 'Muda só se não houver filhos (de sintético) nem uso (de analítico).'}
          </p>
        </div>

        <div>
          <Label htmlFor="c-natureza" className={ROTULO}>Natureza</Label>
          <select id="c-natureza" className={SELECT} disabled={desabilitado} value={natureza} onChange={(e) => aoMudarNatureza(e.target.value as CentroArvoreFormValores['natureza'])}>
            <option value="">Não informada</option>
            {CENTRO_NATUREZAS.map((n) => (
              <option key={n} value={n}>
                {ROTULO_CENTRO_NATUREZA[n]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="c-classificacao" className={ROTULO}>Classificação</Label>
          <select
            id="c-classificacao"
            className={SELECT}
            disabled={desabilitado || !analitico || !produtivo}
            value={classificacao}
            onChange={(e) => aoMudarClassificacao(e.target.value as CentroArvoreFormValores['classificacao'])}
            aria-describedby="c-classificacao-dica"
          >
            <option value="">Não informada</option>
            {CENTRO_CLASSIFICACOES.map((c) => (
              <option key={c} value={c}>
                {ROTULO_CENTRO_CLASSIFICACAO[c]}
              </option>
            ))}
          </select>
          <p id="c-classificacao-dica" className={DICA}>{analitico && produtivo ? 'Só em analítico produtivo.' : 'Só em analítico de natureza produtiva.'}</p>
        </div>

        <div>
          <Label htmlFor="c-trabalho" className={ROTULO}>Tipo de centro de trabalho</Label>
          <select
            id="c-trabalho"
            className={SELECT}
            disabled={desabilitado || !analitico}
            aria-invalid={errors.tipoTrabalho ? true : undefined}
            aria-describedby="c-trabalho-dica c-trabalho-erro"
            {...form.register('tipoTrabalho')}
          >
            {/* Marcador, nao escolha: o analitico nao aceita "vazio" (a API recusa). */}
            <option value="" disabled>
              {analitico ? 'Selecione' : 'Não se aplica'}
            </option>
            {CENTRO_TRABALHO_TIPOS.map((t) => (
              <option key={t} value={t}>
                {ROTULO_CENTRO_TRABALHO[t]}
              </option>
            ))}
          </select>
          <MensagemCampo id="c-trabalho-erro" mensagem={errors.tipoTrabalho?.message} />
          <p id="c-trabalho-dica" className={DICA}>Obrigatório no analítico: define como os minutos são calculados.</p>
        </div>

        <div>
          <Label htmlFor="c-area" className={ROTULO}>Área m²</Label>
          <Input id="c-area" inputMode="decimal" disabled={travaMinutosArea} aria-invalid={errors.areaM2 ? true : undefined} aria-describedby="c-area-erro" {...form.register('areaM2')} />
          <MensagemCampo id="c-area-erro" mensagem={errors.areaM2?.message} />
        </div>

        <div>
          <Label htmlFor="c-minutos" id="c-minutos-rotulo" className={ROTULO}>Minutos disponíveis por mês</Label>
          {minutosDoCentro && minutos.isError ? (
            <div role="alert" className="flex flex-wrap items-center gap-2 rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">
              Não foi possível calcular os minutos.
              <Button type="button" variant="outline" size="sm" onClick={() => void minutos.refetch()}>
                Tentar de novo
              </Button>
            </div>
          ) : minutosDoCentro && minutos.data?.origem === 'calculado' ? (
            <MinutosCalculados dados={minutos.data} />
          ) : minutosDoCentro && minutos.data === undefined ? (
            <p role="status" className={DICA}>Calculando minutos…</p>
          ) : (
            <>
              <Input id="c-minutos" inputMode="decimal" disabled={travaMinutosArea} aria-invalid={errors.minutosDisponiveisMes ? true : undefined} aria-describedby="c-minutos-erro" {...form.register('minutosDisponiveisMes')} />
              <MensagemCampo id="c-minutos-erro" mensagem={errors.minutosDisponiveisMes?.message} />
              {!analitico && <p className={DICA}>Sintético não tem minutos.</p>}
              {minutosDoCentro && minutos.data?.origem === 'manual' && (
                <>
                  <p className="mt-1.5">
                    <span data-testid="minutos-origem" className="rounded-full bg-warning-bg px-2 py-0.5 text-micro text-warning-ink">
                      manual — nenhuma pessoa ou máquina alocada
                    </span>
                  </p>
                  <p className={DICA}>Aloque pessoas e imobilizados a este centro para calcular automaticamente</p>
                </>
              )}
            </>
          )}
        </div>

        {analitico && (
          <div>
            <Label htmlFor="c-ociosidade" className={ROTULO}>Ociosidade (%)</Label>
            {centro ? (
              <>
                <Input id="c-ociosidade" inputMode="decimal" disabled={desabilitado} aria-invalid={errors.ociosidade ? true : undefined} aria-describedby="c-ociosidade-dica c-ociosidade-erro" {...form.register('ociosidade')} />
                <MensagemCampo id="c-ociosidade-erro" mensagem={errors.ociosidade?.message} />
                <p id="c-ociosidade-dica" className={DICA}>Vazio = ociosidade padrão da empresa (Empresa › Parâmetros). Reduz os minutos disponíveis no custo por minuto.</p>
              </>
            ) : (
              <p id="c-ociosidade-dica" className={DICA}>Disponível após salvar.</p>
            )}
          </div>
        )}

        <div className="flex flex-col justify-end pb-1.5">
          <label htmlFor="c-ativo" className="flex items-center gap-2 text-small text-ink-body">
            <input id="c-ativo" type="checkbox" className="size-4" disabled={desabilitado} {...form.register('ativo')} />
            Ativo
          </label>
          {desativaFilhos && <p className={DICA}>Desativar este sintético desativa também os {filhos} {filhos === 1 ? 'filho' : 'filhos'}.</p>}
        </div>
      </form>

      {erro && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">
          {erro}
        </p>
      )}

      {centro && (
        <div className="border-t border-border pt-4">
          <CodigosExternos entidade="centros-custo" id={centro.id} editavel={editavel} />
        </div>
      )}

      {editavel && (
        <RecordToolbar
          dirty={isDirty}
          saving={criar.isPending || atualizar.isPending}
          savedAt={salvoEm}
          erro={erro}
          novo={!centro}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty ? Object.keys(dirtyFields).length : undefined}
          onSave={salvar}
          onUndo={() => {
            form.reset(base);
            setErro(null);
          }}
          onNew={onNovo}
          {...(centro && onExcluido
            ? {
                onDelete: () => void aoExcluir(),
                excluirRotulo: 'Excluir centro',
                excluirTitulo: podeExcluir ? `Excluir ${centro.nome}?` : `Desativar ${centro.nome}?`,
                excluirConfirmar: podeExcluir ? 'Excluir' : 'Desativar',
                excluirDescricao: !uso.data ? (
                  'Verificando o uso do centro…'
                ) : podeExcluir ? (
                  'Sem filhos, CPM, etapas de ficha, lotes nem origem importada: o centro sai do cadastro.'
                ) : (
                  <>
                    Não pode ser excluído porque tem {motivos.join(', ')}. Ele será desativado (nada é apagado; marque Ativo para reativar).
                    {centro.tipoNo === 'SINTETICO' && filhos > 0 && ' Desativar um sintético desativa também a subárvore.'}
                  </>
                ),
              }
            : {})}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />
    </section>
  );
}

/** Minutos calculados pela API (origem `calculado`): so leitura. Os numeros vem prontos; aqui so formata e conta. */
function MinutosCalculados({ dados }: { dados: MinutosCentroResponse }) {
  const { pessoasAlocadas: p, imobilizadosAlocados: m } = dados;
  const alocados = `${p} ${p === 1 ? 'pessoa' : 'pessoas'} e ${m} ${m === 1 ? 'imobilizado' : 'imobilizados'} alocados`;
  return (
    <div role="group" aria-labelledby="c-minutos-rotulo" className="rounded-md border border-border bg-line-soft px-3 py-2">
      <p className="flex items-baseline gap-2">
        <span data-testid="minutos-total" className="tabular text-metric font-semibold">
          {formatarQuantidade(dados.total)}
        </span>
        <span className="text-small text-ink-secondary">min/mês</span>
        <span data-testid="minutos-origem" className="ml-auto rounded-full bg-secondary px-2 py-0.5 text-micro text-ink-body">
          calculado
        </span>
      </p>
      <p data-testid="minutos-detalhe" className="mt-1 text-caption text-ink-muted">
        pessoas {formatarQuantidade(dados.minutosPessoas)} min + máquinas {formatarQuantidade(dados.minutosMaquinas)} min
      </p>
      <p className="text-caption text-ink-muted">{alocados}</p>
      <p className={DICA}>Calculado a partir das alocações; não se edita aqui.</p>
    </div>
  );
}
